// LocationSearch — one search box over continents, countries, regions, states and cities. Picking a
// result hands the caller a fully spelled place (continent / country / state / city), which the
// LocationSelector adds as a normal location row below — the same row choosing each dropdown by hand
// would produce. Reuses the City box's dropdown styles; no flags or logos, text only.
import { useEffect, useId, useRef, useState } from 'react';
import { useLocationSearch } from '@/hooks/useLocations';
import type { LocationSearchResult } from '@/types';

export interface LocationSearchProps {
  onPick: (result: LocationSearchResult) => void;
}

export default function LocationSearch({ onPick }: LocationSearchProps) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const { data: results = [], isFetching } = useLocationSearch(text);

  const showList = open && text.trim().length >= 2;

  useEffect(() => {
    setActive(0);
  }, [results]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const pick = (result: LocationSearchResult) => {
    onPick(result);
    setText('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && showList) {
      e.preventDefault();
      pick(results[active]);
    }
  };

  const optionId = (i: number) => `${listboxId}-opt-${i}`;

  return (
    <div
      className="city-input-wrapper position-relative mb-3"
      ref={wrapperRef}
      role="combobox"
      aria-expanded={showList}
      aria-haspopup="listbox"
      aria-controls={listboxId}
    >
      <input
        type="search"
        className="form-control rounded-pill location-text-input pe-5"
        placeholder="Search a continent, country, region, state or city"
        aria-label="Search locations"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={showList && results.length ? optionId(active) : undefined}
        autoComplete="off"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <i
        className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
        aria-hidden="true"
      />
      <div
        id={listboxId}
        className={`custom-dropdown-content${showList ? ' show' : ''}`}
        role="listbox"
        aria-label="Location suggestions"
      >
        {results.map((r, i) => (
          <button
            key={`${r.type}-${r.label}-${r.detail}`}
            id={optionId(i)}
            type="button"
            role="option"
            aria-selected={i === active}
            className={`custom-dropdown-item${i === active ? ' selected' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(i)}
            onClick={() => pick(r)}
          >
            <span className="d-block">{r.label}</span>
            <span className="d-block small opacity-75">{r.detail}</span>
          </button>
        ))}
        {showList && !results.length && (
          <div className="custom-dropdown-item text-muted" role="status">
            {isFetching ? 'Searching…' : 'No matching place'}
          </div>
        )}
      </div>
    </div>
  );
}
